import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { defineMessages, t, T } from '@sm/intl';
import { Button, Card, Col, Grid, Row, Toast, ToastBody, ToastTitle, Typography } from '@sm/wds-react';
import { generateMetricsAttribute } from '@sm/metrics';
import EMAIL_PREFERENCE_CATEGORY from '../enums';
import './email-preferences-list.scss';

import EmailPreferenceItem from './EmailPreferenceItem';
import UnsubscribeConfirmationModal from './UnsubscribeConfirmationModal';
import UpdatePreferencesButton from '../UpdatePreferencesButton';
import * as EmailPreferencesToast from '../EmailPreferencesToast';

const COPY = defineMessages({
  NEWSLETTER_LABEL: {
    id: 'User.EmailPreferencesList.NewsletterLabel',
    defaultMessage: 'Monthly Newsletters',
    description: '[Type: Label][Vis.: med] - Marketing email preferences newsletter category label',
  },
  NEWSLETTER_DESCRIPTION: {
    id: 'User.EmailPreferencesList.NewsletterDescription',
    defaultMessage:
      'A monthly round up of best practices, survey tips, product updates, stories from successful customers, and more.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences newsletter category description',
  },
  SURVEY_BEST_PRACTICES_LABEL: {
    id: 'User.EmailPreferencesList.SurveyBestPracticesLabel',
    defaultMessage: 'Best Practices',
    description: '[Type: Label][Vis.: med] - Marketing email preferences survey best practices category label',
  },
  SURVEY_BEST_PRACTICES_DESCRIPTION: {
    id: 'User.EmailPreferencesList.SurveyBestPracticesDescription',
    defaultMessage:
      'Tips and techniques for creating powerful surveys, industry research, leadership stories, and ideas to help you build a thriving workplace.',
    description:
      '[Type: Paragraph][Vis.: med] - Marketing email preferences survey best practices category description',
  },
  DATA_STORIES_LABEL: {
    id: 'User.EmailPreferencesList.DataStoriesLabel',
    defaultMessage: 'Data Stories and Quizzes',
    description: '[Type: Label][Vis.: med] - Marketing email preferences data stories category label',
  },
  DATA_STORIES_DESCRIPTION: {
    id: 'User.EmailPreferencesList.DataStoriesDescription',
    defaultMessage:
      'Insights from research on trending topics, stories from influencers and industry leaders, and invitations to take quizzes on current trends.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences data stories category description',
  },
  CUSTOMER_OPPORTUNITIES_LABEL: {
    id: 'User.EmailPreferencesList.CustomerOpportunitiesLabel',
    defaultMessage: 'Product Updates',
    description: '[Type: Label][Vis.: med] - Marketing email preferences customer opportunities category label',
  },
  CUSTOMER_OPPORTUNITIES_DESCRIPTION: {
    id: 'User.EmailPreferencesList.CustomerOpportunitiesDescription',
    defaultMessage: "Updates on what's new in the product, invitations to webinars, and discount offers.",
    description:
      '[Type: Paragraph][Vis.: med] - Marketing email preferences customer opportunities category description',
  },
  PURCHASE_INQUIRIES_LABEL: {
    id: 'User.EmailPreferencesList.PurchaseInquiriesLabel',
    defaultMessage: 'Enterprise Solutions',
    description: '[Type: Label][Vis.: med] - Marketing email preferences purchase inquiries category label',
  },
  PURCHASE_INQUIRIES_DESCRIPTION: {
    id: 'User.EmailPreferencesList.PurchaseInquiriesDescription',
    defaultMessage:
      'Personalized recommendations for innovative solutions and integrations that can bring more value to your organization.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences purchase inquiries category description',
  },
  PRODUCT_RESEARCH_LABEL: {
    id: 'User.EmailPreferencesList.ProductResearchLabel',
    defaultMessage: 'Product Feedback and Research',
    description: '[Type: Label][Vis.: med] - Marketing email preferences product research category label',
  },
  PRODUCT_RESEARCH_DESCRIPTION: {
    id: 'User.EmailPreferencesList.ProductResearchDescription',
    defaultMessage: 'Invitations to tell us about your experience with Momentive products* and help us improve them.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences product research category description',
  },
  UNSUBSCRIBE_FROM_ALL_BUTTON: {
    id: 'User.EmailPreferencesList.UnsubscribeFromAllButton',
    defaultMessage: 'UNSUBSCRIBE FROM ALL',
    description: '[Type: Button][Vis.: med] - Marketing email preferences unsubscribe from all button',
  },
  ACCOUNT_RELATED_EMAILS_TEXT: {
    id: 'User.EmailPreferencesList.AccountRelatedEmailsText',
    defaultMessage:
      'If you have an account for SurveyMonkey or any other Momentive product, we’ll continue to send you account-related emails.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences account-related emails text',
  },
  MOMENTIVE_PRODUCTS_TEXT: {
    id: 'User.EmailPreferencesList.MomentiveProductsText',
    defaultMessage:
      '* Momentive products include SurveyMonkey, GetFeedback, Apply, TechValidate, Wufoo, and Momentive solutions for brand insights and market insights.',
    description: '[Type: Paragraph][Vis.: med] - Marketing email preferences Momentive products text',
  },
  EMAIL_PREFERENCES_CARD_HEADER: {
    id: 'User.EmailPreferencesList.CardHeader',
    defaultMessage: 'Email Preferences for {email}',
    description: '[Type: Header][Vis.: high] - Marketing email preferences card header displaying email address',
  },
  UPDATE_PREFERENCES_BUTTON_TITLE: {
    id: 'User.EmailPreferencesList.UpdatePreferencesButton',
    defaultMessage: 'UPDATE PREFERENCES',
    description: '[Type: Button][Vis.: med] - Marketing email preferences update preferences button',
  },
  UNSUBSCRIBE_BUTTON_TITLE: {
    id: 'User.EmailPreferencesList.UnsubscribeButton',
    defaultMessage: 'UNSUBSCRIBE',
    description:
      '[Type: Button][Vis.: med] - Marketing email preferences unsubscribe confirmation modal unsubscribe button',
  },
});

class EmailPreferencesList extends Component {
  static propTypes = {
    emailPreferences: PropTypes.shape({
      newsletter: PropTypes.bool,
      surveyBestPractices: PropTypes.bool,
      dataStories: PropTypes.bool,
      customerOpportunities: PropTypes.bool,
      purchaseInquiries: PropTypes.bool,
      productResearch: PropTypes.bool,
    }).isRequired,
    encryptedEmail: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    origin: PropTypes.string,
    campaignId: PropTypes.string,
    initialCategory: PropTypes.string,
  };

  static defaultProps = {
    origin: null,
    campaignId: null,
    initialCategory: null,
  };

  constructor(props) {
    super(props);

    const { emailPreferences, initialCategory } = props;

    // Only show modal if user is subscribed to the category
    const showModal = !!emailPreferences[initialCategory];

    const selectedPreferences = {};
    Object.keys(EMAIL_PREFERENCE_CATEGORY).forEach(category => {
      selectedPreferences[category] = initialCategory === category ? false : emailPreferences[category];
    });

    this.state = {
      selectedPreferences,
      showModal,
      showToast: false,
      toast: {},
    };
  }

  handleCheckboxChange = e => {
    const { id, checked } = e.target;
    this.updateSelectedPreference(id, checked);
  };

  updateSelectedPreference = (category, isSelected) => {
    this.setState(prevState => ({
      selectedPreferences: {
        ...prevState.selectedPreferences,
        [category]: isSelected,
      },
    }));
  };

  unsetAllCategories = () => {
    this.setState(prevState => {
      const selectedPreferences = Object.assign(
        {},
        ...Object.keys(prevState.selectedPreferences).map(category => ({
          [category]: false,
        }))
      );
      return {
        ...prevState,
        selectedPreferences,
      };
    });
  };

  selectionIsUnchanged = () => {
    const { emailPreferences } = this.props;
    const { selectedPreferences } = this.state;
    return Object.keys(selectedPreferences).every(key => selectedPreferences[key] === emailPreferences[key]);
  };

  getCategoriesChanged = () => {
    const { emailPreferences } = this.props;
    const { selectedPreferences } = this.state;

    const categoriesChanged = {};
    Object.keys(selectedPreferences).forEach(category => {
      if (emailPreferences[category] !== selectedPreferences[category]) {
        categoriesChanged[category] = selectedPreferences[category];
      }
    });

    return categoriesChanged;
  };

  getToast = error => {
    if (error) return EmailPreferencesToast.GeneralError();

    // Toast content varies based on the number of categories changed
    const { selectedPreferences } = this.state;
    const { emailPreferences } = this.props;

    if (Object.values(selectedPreferences).every(value => value)) return EmailPreferencesToast.SubscribeAll();

    if (Object.values(selectedPreferences).every(value => !value)) return EmailPreferencesToast.UnsubscribeAll();

    const categoriesChanged = Object.keys(selectedPreferences).filter(
      category => emailPreferences[category] !== selectedPreferences[category]
    );
    if (categoriesChanged.length === 1) {
      const category = categoriesChanged[0];
      const label = t(COPY[`${EMAIL_PREFERENCE_CATEGORY[category]}_LABEL`]);
      if (selectedPreferences[category]) {
        return EmailPreferencesToast.SubscribedCategory(label);
      }
      return EmailPreferencesToast.UnsubscribedCategory(label);
    }

    return EmailPreferencesToast.MultipleChanged();
  };

  closeModal = () => {
    this.setState({
      showModal: false,
    });
  };

  handleComplete = result => {
    const error = result === 'error';
    this.closeModal();
    this.openToast(error);
  };

  openToast = showError =>
    this.setState({
      showToast: true,
      toast: this.getToast(showError),
    });

  closeToast = () => this.setState({ showToast: false, toast: {} });

  renderToast = () => {
    const { showToast } = this.state;
    const { color, title, body, dismissible } = this.state.toast;

    return (
      <Toast show={showToast} color={color} dismissible={dismissible} onClose={this.closeToast}>
        <ToastTitle>{title}</ToastTitle>
        <ToastBody>{body}</ToastBody>
      </Toast>
    );
  };

  renderUnsubscribeButton = (title, flow) => {
    const { encryptedEmail, origin, campaignId } = this.props;
    const { selectedPreferences } = this.state;

    return (
      <UpdatePreferencesButton
        encryptedEmail={encryptedEmail}
        emailPreferences={selectedPreferences}
        categoriesChanged={this.getCategoriesChanged()}
        origin={origin}
        campaignId={campaignId}
        flow={flow}
        disabled={this.selectionIsUnchanged()}
        handleSuccess={() => this.handleComplete('success')}
        handleError={() => this.handleComplete('error')}
      >
        {title}
      </UpdatePreferencesButton>
    );
  };

  renderConfirmationModal = () => {
    const { initialCategory } = this.props;
    const { showModal } = this.state;

    const handleCancel = () => {
      this.updateSelectedPreference(initialCategory, true);
      this.closeModal();
    };

    return (
      <UnsubscribeConfirmationModal
        show={showModal}
        category={initialCategory}
        submitButton={this.renderUnsubscribeButton(t(COPY.UNSUBSCRIBE_BUTTON_TITLE), 'confirmation_modal')}
        handleCancel={handleCancel}
      />
    );
  };

  renderTitle = () => {
    const { email } = this.props;
    return (
      <Grid fluid noBleeds>
        <Row>
          <Col sm={8}>
            <Typography variant="cardTitle" className="wds-p-b-2 sm-email-preferences-list__card-title">
              <T desc={COPY.EMAIL_PREFERENCES_CARD_HEADER} values={{ email }} />
            </Typography>
          </Col>
          <Col sm={4}>{this.renderUnsubscribeButton(t(COPY.UPDATE_PREFERENCES_BUTTON_TITLE), 'category_list')}</Col>
        </Row>
      </Grid>
    );
  };

  render() {
    const { selectedPreferences } = this.state;

    return (
      <Card title={this.renderTitle()}>
        {Object.entries(EMAIL_PREFERENCE_CATEGORY).map(([category, label]) => (
          <EmailPreferenceItem
            title={t(COPY[`${label}_LABEL`])}
            description={t(COPY[`${label}_DESCRIPTION`])}
            isSubscribed={selectedPreferences[category]}
            name={category}
            key={category}
            handleChange={this.handleCheckboxChange}
          />
        ))}
        <Button
          className="wds-m-b-6 sm-marketing-email-preferences__unsubscribe-all-button"
          color="alt"
          onClick={this.unsetAllCategories}
          data-testid="EmailPreferencesList__UnsubscribeAllButton"
          data-sm-metrics={generateMetricsAttribute({
            data: {
              actionFlow: 'marketing_email_preferences',
              actionType: 'unsubscribe_all.button_click',
            },
          })}
        >
          <T desc={COPY.UNSUBSCRIBE_FROM_ALL_BUTTON} />
        </Button>
        <Typography variant="bodySm" color="dark-muted">
          <T desc={COPY.ACCOUNT_RELATED_EMAILS_TEXT} />
        </Typography>
        <Typography className="wds-m-t-4 wds-m-b-6" variant="bodySm" color="dark-muted">
          <T desc={COPY.MOMENTIVE_PRODUCTS_TEXT} html />
        </Typography>
        {this.renderConfirmationModal()}
        {this.renderToast()}
      </Card>
    );
  }
}

export default EmailPreferencesList;
